import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocations } from '@/hooks/use-locations';
import { useLocationStore } from '@/stores/location-store';
import { setLocationTimeZone } from '@/lib/report-timezone';

/**
 * Bridges the active location's configured timezone (Super Admin per-location
 * setting) into the global report-timezone singleton. Once mounted, the whole
 * app — the DatePicker's default zone, report day-boundary math, and every
 * date/time display that reads the singleton — follows the selected location's
 * zone with no per-page wiring.
 *
 * Renders nothing. Mount once inside the authenticated app shell (useLocations
 * needs an authenticated session; the ['locations'] query is shared/deduped
 * with the rest of the app).
 */
export function TimezoneSync() {
  const { selectedLocationId } = useLocationStore();
  const { data } = useLocations();
  const queryClient = useQueryClient();

  const locations = data?.data ?? [];
  const activeTimeZone =
    (locations.find((l: { id: string; timezone?: string }) => l.id === selectedLocationId)?.timezone as
      | string
      | undefined) ?? null;

  useEffect(() => {
    setLocationTimeZone(activeTimeZone);
    // The reporting zone is applied inside the query layer's day-boundary math
    // (withLocalDateRange), whose keys don't carry the zone — so nudge every POS
    // report to refetch with the new location zone. A no-op when none are mounted.
    queryClient.invalidateQueries({
      predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith('pos'),
    });
  }, [activeTimeZone, queryClient]);

  return null;
}
